import DPO from 'dpo8core';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { addError } from '@app/actions/popupsActions';
import { setFooterConfig } from '@app/actions/footerActions';
import { submitATBCodesRequestForm } from '@app/actions/applyCodesActions';
import { processFinishedArtwork, finishArtwork } from '@app/actions/artworkDataActions';
import { APPLY_CODES_FLOW_SELECTION, AUTHORIZE_CODES_FLOW } from '@app/constants';
import { inject } from '@app/providers/QAU.ContextProvider';
import { isJsonStr } from '@app/utils/utils';
import LocalizedText from '@LocalizedText';
import useSaveToYourAccount from '@app/hooks/use-save-to-your-account';

import RequestCodesForm from './request-codes-form/RequestCodesForm';
import './RequestCodes.scss';

const RequestCodes = ({
  submitATBCodesRequestForm,
  processFinishedArtwork,
  selectApplyCodesStep,
  brandIdList = [],
  isAuthorized,
  setFooterConfig,
  finishArtwork,
  flowConfig,
  addError,
  context,
  flow,
  matrix,
}) => {
  const [formValid, setFormValid] = useState(false);
  const [formData, setFormData] = useState(null);

  const { current: brandIdExists } = useRef(!!brandIdList.length);

  const returnToApplyCodesFlowSelection = useCallback(() => {
    selectApplyCodesStep(APPLY_CODES_FLOW_SELECTION);
  }, [selectApplyCodesStep]);

  const createRequestFormObject = useCallback(() => {
    const {
      sku,
      gtin,
      brand_id,
      codes_number,
    } = formData;

    return {
      numberOfCodes: Number(codes_number),
      brandIdentifier: brand_id.trim(),
      gtin,
      sku,
    };
  }, [formData]);

  const onRequestFormSubmitError = useCallback(
    (errors = []) => {
      const [error = {}] = errors;
      const { message: errorBody } = error;

      if (isJsonStr(errorBody)) {
        const { errorCode = '', message: errorMessage } = JSON.parse(errorBody);
        const errorLocKey = `qau-${errorCode.toLowerCase().replace(/_/g, '-')}`;
        addError(!errorMessage && errorLocKey, { message: errorMessage });
      } else {
        addError(null, { message: errorBody.message });
      }
    },
    [addError],
  );

  const onRequestFormSubmitSuccess = useCallback(
    ({ data: atbCodesData }) => {
      const { projectData } = flow;
      projectData.mailMergeData = atbCodesData;
      projectData.mailMergeData.hint.push({ name: 'barcodeType', value: 'UPC-A' });
      projectData.mailMergeData.name = 'ATB Over Labeling';

      const mmData = DPO.mmd.Data.fromObject(projectData.mailMergeData);
      const provider = new DPO.mmd.DataSourceProvider(mmData);

      const newContext = context.createNew(flow.project, projectData, provider);
      newContext.storage.imageType = projectData.product.productProfile === 'DEFAULT' ? 'low' : 'cmyk';

      finishArtwork(flow, newContext, flowConfig).then(artworkData => {
        setFooterConfig({ backDisabled: true });
        processFinishedArtwork(artworkData, flow, flowConfig);
      });
    },
    [flow, flowConfig, context, finishArtwork, setFooterConfig, processFinishedArtwork],
  );

  const submitRequestCodesForm = useCallback(() => {
    const { projectData } = context;
    const requestFormData = createRequestFormObject();

    submitATBCodesRequestForm(requestFormData, projectData).then(onRequestFormSubmitSuccess).catch(onRequestFormSubmitError);
  }, [context, createRequestFormObject, submitATBCodesRequestForm, onRequestFormSubmitError, onRequestFormSubmitSuccess]);

  useEffect(() => {
    setFooterConfig({
      visible: true,
      nextHidden: false,
      backHidden: false,
      nextDisabled: !formValid,
      nextLocKey: 'qau-finish-btn',
      onNext: submitRequestCodesForm,
      onBack: returnToApplyCodesFlowSelection,
    });
  }, [formValid, setFooterConfig, returnToApplyCodesFlowSelection, submitRequestCodesForm]);

  const { navigateToLoginSignup } = useSaveToYourAccount({ matrix });

  useEffect(() => {
    !brandIdExists && selectApplyCodesStep(AUTHORIZE_CODES_FLOW);
    !isAuthorized && navigateToLoginSignup();
  }, [brandIdExists, isAuthorized, navigateToLoginSignup, selectApplyCodesStep]);

  return brandIdExists ? (
    <div id="qau-request-codes">
      <div className="request-codes-title">
        <LocalizedText id="qau-request-codes-title" />
      </div>
      <RequestCodesForm {...{ brandIdList, setFormData, setFormValid }} />
      <div className="request-codes-submit-desc">
        <LocalizedText id="qau-request-codes-submit-description" />
      </div>
    </div>
  ) : null;
};

const mapStateToProps = ({ applyCodes: { brandIdList }, auth: { isAuthorized }, matrix }) => ({ brandIdList, isAuthorized, matrix });

const mapDispatchToProps = {
  submitATBCodesRequestForm,
  processFinishedArtwork,
  setFooterConfig,
  finishArtwork,
  addError,
};

RequestCodes.propTypes = {
  brandIdList: PropTypes.array,
  flow: PropTypes.object.isRequired,
  addError: PropTypes.func.isRequired,
  context: PropTypes.object.isRequired,
  flowConfig: PropTypes.object.isRequired,
  matrix: PropTypes.object.isRequired,
  finishArtwork: PropTypes.func.isRequired,
  isAuthorized: PropTypes.bool.isRequired,
  setFooterConfig: PropTypes.func.isRequired,
  selectApplyCodesStep: PropTypes.func.isRequired,
  processFinishedArtwork: PropTypes.func.isRequired,
  submitATBCodesRequestForm: PropTypes.func.isRequired,
};

export default inject('flow', 'flowConfig', 'context')(connect(mapStateToProps, mapDispatchToProps)(RequestCodes));
